import * as React from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  FarmlySheet,
  FarmlySheetBody,
} from "@/components/ui/FarmlySheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const MAX_WIDTH_MAP = {
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
} as const;

export interface ResponsiveOverlayProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  /** Desktop presentation: "dialog" (centered) or "sidepanel" (right-aligned). Default: "dialog" */
  desktopVariant?: "dialog" | "sidepanel";
  /** @deprecated Use desktopVariant instead */
  variant?: "dialog" | "sidepanel";
  /** Max-width for desktop dialog. Default: "lg" */
  desktopMaxWidth?: "md" | "lg" | "xl" | "2xl";
  /** Title for accessibility (required by Radix). Rendered in header when showHeader is true. */
  title?: string;
  /** Optional description below title */
  description?: string;
  /** Whether to render default header with title. Default: false */
  showHeader?: boolean;
  /** Additional className for the content wrapper */
  className?: string;
  /** Sheet side on mobile. Default: "bottom" */
  mobileSide?: "bottom" | "right" | "left";
}

/**
 * Responsive overlay following Farmly Overlay Rule:
 * - < md (mobile): Bottom Sheet (FarmlySheet) with grabber, dvh, safe-area, iOS keyboard handling
 * - ≥ md (desktop): Dialog (centered) or Side Panel (right-aligned)
 *
 * Mobile patterns never scale up to desktop. Desktop never shows bottom sheets.
 */
export function ResponsiveOverlay({
  open,
  onOpenChange,
  children,
  desktopVariant,
  variant,
  desktopMaxWidth = "lg",
  title,
  description,
  showHeader = false,
  className,
  mobileSide = "bottom",
}: ResponsiveOverlayProps) {
  const isMobile = useIsMobile();
  const resolvedVariant = desktopVariant || variant || "dialog";

  // ─── Mobile: FarmlySheet (iOS-safe) ────────────────
  if (isMobile) {
    return (
      <FarmlySheet
        open={open}
        onOpenChange={onOpenChange}
        side={mobileSide}
        title={title}
        description={description}
        showHeader={showHeader}
        className={className}
      >
        <FarmlySheetBody>
          {children}
        </FarmlySheetBody>
      </FarmlySheet>
    );
  }

  // ─── Desktop: Side Panel ────────────────────────────
  if (resolvedVariant === "sidepanel") {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className={cn(
            "w-full max-w-[480px] max-h-screen overflow-y-auto p-6",
            className,
          )}
        >
          {showHeader && title && (
            <SheetHeader className="pb-4">
              <SheetTitle>{title}</SheetTitle>
              {description && <SheetDescription>{description}</SheetDescription>}
            </SheetHeader>
          )}
          {!showHeader && title && (
            <SheetHeader className="sr-only">
              <SheetTitle>{title}</SheetTitle>
            </SheetHeader>
          )}
          {children}
        </SheetContent>
      </Sheet>
    );
  }

  // ─── Desktop: Dialog (centered) ─────────────────────
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          MAX_WIDTH_MAP[desktopMaxWidth],
          "max-h-[80vh] overflow-y-auto p-6 rounded-2xl",
          className,
        )}
      >
        {showHeader && title && (
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {description && <DialogDescription>{description}</DialogDescription>}
          </DialogHeader>
        )}
        {!showHeader && title && (
          <DialogHeader className="sr-only">
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
        )}
        {children}
      </DialogContent>
    </Dialog>
  );
}
